// Maturity calculator (MAT-04..06, docs/maturity/02). Pure and shared by the server (authoritative results,
// snapshots, exports) and the client (unsaved previews). yes=2, partial=1, no=0 half-point units; a category is
// 100·units/(2·count); every parent is the equal mean of its direct children; any blank makes the branch pending.
import type { Definition } from "./definition.ts";
import type { Answer } from "./responses.ts";
import { cmp, cmpInt, fraction, fromStrings, mean, sub, toNumber, toStrings, type Fraction } from "./fraction.ts";

export const UNITS: Record<Answer, bigint> = { yes: 2n, partial: 1n, no: 0n };
export const POINTS: Record<Answer, number> = { yes: 5, partial: 2.5, no: 0 };
export const ANSWER_LABEL: Record<Answer, string> = { yes: "Sim", partial: "Em parte", no: "Não" };
export const NO_ANSWER_LABEL = "Sem resposta";
export const PENDING = "Pendente";

export function categoryFraction(answers: (Answer | null)[]): Fraction | null {
  if (!answers.length || answers.some((a) => a === null)) return null;
  const units = (answers as Answer[]).reduce((s, a) => s + UNITS[a], 0n);
  return fraction(100n * units, 2n * BigInt(answers.length));
}

export function adoption(f: Fraction | null): string {
  if (!f) return PENDING;
  if (cmpInt(f, 0) === 0) return "Não adota";
  if (cmpInt(f, 50) < 0) return "Adota em menor parte";
  if (cmpInt(f, 50) === 0) return "Adota parcialmente";
  if (cmpInt(f, 100) < 0) return "Adota em maior parte";
  return "Adota totalmente";
}

export function situation(f: Fraction | null): string {
  if (!f) return PENDING;
  if (cmpInt(f, 0) === 0) return "Inexpressivo";
  if (cmpInt(f, 50) <= 0) return "Intermediário";
  if (cmpInt(f, 100) < 0) return "Aperfeiçoado";
  return "Avançado";
}

/** Wire form of a score: numeric percent for rendering plus the exact fraction (strings) for comparison. */
export interface ScoreSummary {
  percent: number | null;
  fraction: [string, string] | null;
  adoption: string;
  situation: string;
  answered: number;
  total: number;
}
export interface CategoryScore extends ScoreSummary { id: string; code: string; title: string }
export interface SubdimensionScore extends ScoreSummary { id: string; code: string; title: string; categories: CategoryScore[] }
export interface DimensionScore extends ScoreSummary { id: string; code: string; title: string; subdimensions: SubdimensionScore[] }

const summary = (f: Fraction | null, answered: number, total: number): ScoreSummary => ({
  percent: f ? toNumber(f) : null,
  fraction: f ? toStrings(f) : null,
  adoption: adoption(f),
  situation: situation(f),
  answered,
  total,
});

export const summaryFraction = (s: Pick<ScoreSummary, "fraction"> | null | undefined) => (s?.fraction ? fromStrings(s.fraction) : null);

/** Scores the whole tree. `answerOf` returns the saved (or previewed) answer of a question id, or null. */
export function computeScores(def: Pick<Definition, "dimension">, answerOf: (questionId: string) => Answer | null): DimensionScore {
  const d = def.dimension;
  let dimAnswered = 0;
  let dimTotal = 0;
  const subs = d.subdimensions.map((s) => {
    let sa = 0;
    let st = 0;
    const cats = s.categories.map((c) => {
      const answers = c.questions.map((q) => answerOf(q.id));
      const answered = answers.filter((a) => a !== null).length;
      sa += answered;
      st += answers.length;
      return { id: c.id, code: c.code, title: c.title, exact: categoryFraction(answers), answered, total: answers.length };
    });
    dimAnswered += sa;
    dimTotal += st;
    const exact = mean(cats.map((c) => c.exact));
    return { id: s.id, code: s.code, title: s.title, exact, answered: sa, total: st, cats };
  });
  const exact = mean(subs.map((s) => s.exact));
  return {
    id: d.id,
    code: d.code,
    title: d.title,
    ...summary(exact, dimAnswered, dimTotal),
    subdimensions: subs.map((s) => ({
      id: s.id,
      code: s.code,
      title: s.title,
      ...summary(s.exact, s.answered, s.total),
      categories: s.cats.map((c) => ({ id: c.id, code: c.code, title: c.title, ...summary(c.exact, c.answered, c.total) })),
    })),
  };
}

/** Prognosis minus diagnosis, in percentage points; null unless both exist (MAT-10, no fake zero). */
export function delta(diagnosis: Fraction | null, prognosis: Fraction | null): Fraction | null {
  return diagnosis && prognosis ? sub(prognosis, diagnosis) : null;
}

export interface DeltaSummary { points: number | null; fraction: [string, string] | null }
export const deltaSummary = (d: ScoreSummary | null | undefined, p: ScoreSummary | null | undefined): DeltaSummary => {
  const v = delta(summaryFraction(d), summaryFraction(p));
  return { points: v ? toNumber(v) : null, fraction: v ? toStrings(v) : null };
};

// ---- presentation (pt-BR, two decimals; never snapped) -------------------------------------------------------

const NF = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const BOUNDARIES = [0, 50, 100];

/**
 * "57,14%". When rounding would land exactly on a classification boundary that the exact value does not reach,
 * a "<"/">" marks it, so 99,996% never reads as fully adopted.
 */
export function formatPercent(s: Pick<ScoreSummary, "fraction"> | null | undefined, suffix = "%"): string {
  const f = summaryFraction(s);
  if (!f) return PENDING;
  const x = toNumber(f);
  const rounded = Math.round(x * 100) / 100;
  let prefix = "";
  for (const b of BOUNDARIES) {
    if (rounded === b && cmpInt(f, b) !== 0) prefix = cmpInt(f, b) < 0 ? "< " : "> ";
  }
  return `${prefix}${NF.format(rounded === 0 ? 0 : rounded)}${suffix}`;
}

/** "+37,50" / "-50,00" / "0,00" (sign from the exact value). */
export function formatDelta(d: Pick<DeltaSummary, "fraction"> | null | undefined): string | null {
  if (!d?.fraction) return null;
  const f = fromStrings(d.fraction);
  const sign = cmp(f, fraction(0n));
  const body = NF.format(Math.abs(toNumber(f)));
  return sign > 0 ? `+${body}` : sign < 0 ? `-${body}` : body;
}

export const formatPoints = (a: Answer | null | undefined) => (a ? String(POINTS[a]).replace(".", ",") : "");
